import { Box, Typography, Chip, Tooltip } from '@mui/material';
import type { PathogenClassificationResult } from '@/services/api/testService';
import type { ReferenceLabelDto } from '@/types/referenceLabels';

const STATUS_COLOR: Record<string, 'error' | 'success' | 'warning' | 'default'> = {
  positive: 'error',
  negative: 'success',
  inconclusive: 'warning',   // keep for any pre-existing data
  rejected: 'warning',
};

const STATUS_LABEL: Record<string, string> = {
  positive: 'Positive',
  negative: 'Negative',
  inconclusive: 'Inconclusive',  // keep for any pre-existing data
  rejected: 'Rejected',
};

export interface ClassificationPanelProps {
  results: PathogenClassificationResult[];
  /** Per-channel color map: key = 'CH1', value = css color string */
  channelColorMap: Map<string, string>;
  /** RESEARCH FEATURE: reference labels map (channelId → full DTO, for label + concentration) */
  referenceLabels?: Map<number, ReferenceLabelDto>;
}

/**
 * Compact classification results panel, shown in Combined view alongside the chart legend.
 * Renders one row per channel that has a result.
 */
export function ClassificationPanel({ results, channelColorMap, referenceLabels }: ClassificationPanelProps) {
  if (!results || results.length === 0) return null;

  // Group by channelId (1-based number → 'CH1', etc.)
  const byChannel = new Map<string, PathogenClassificationResult[]>();
  for (const r of results) {
    const key = `CH${r.channelId}`;
    if (!byChannel.has(key)) byChannel.set(key, []);
    byChannel.get(key)!.push(r);
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 0.75,
      }}
    >
      {Array.from(byChannel.entries()).map(([channelId, chResults]) => {
        const color = channelColorMap.get(channelId);
        return (
          <Box
            key={channelId}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
          >
            {/* Channel color dot + label */}
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: color ?? 'text.disabled',
                flexShrink: 0,
              }}
            />
            <Typography variant="caption" fontWeight={600} sx={{ mr: 0.5 }}>
              {channelId}
            </Typography>

            {/* One chip per pathogen result on this channel */}
            {chResults.map((r) => {
              const status = r.status ?? 'inconclusive';
              const chipColor = STATUS_COLOR[status] ?? 'default';
              const pct = r.confidence != null ? ` ${Math.round(r.confidence * 100)}%` : '';
              const label = `${r.pathogenName || `Channel ${r.channelId}`} · ${STATUS_LABEL[status] ?? status}${pct}`;
              return (
                <Tooltip
                  key={`${r.pathogenName}-${r.channelId}`}
                  title={
                    r.concentration != null
                      ? `Concentration: ${r.concentration.toFixed(2)}`
                      : ''
                  }
                  placement="top"
                  arrow
                >
                  <Chip
                    label={label}
                    color={chipColor}
                    size="small"
                    sx={{ height: 20, fontSize: '0.68rem', fontWeight: 500 }}
                  />
                </Tooltip>
              );
            })}

            {/* RESEARCH FEATURE: Reference label (+ concentration) for this channel */}
            {(() => {
              const dto = referenceLabels?.get(chResults[0]?.channelId);
              if (!dto) return null;
              const labelText = STATUS_LABEL[dto.referenceLabel!] ?? dto.referenceLabel;
              const concSuffix = dto.concentration != null ? ` · ${dto.concentration.toFixed(2)} cp/mL` : '';
              return (
                <Chip
                  label={`Ref: ${labelText}${concSuffix}`}
                  variant="outlined"
                  size="small"
                  color={STATUS_COLOR[dto.referenceLabel!] ?? 'default'}
                  sx={{ height: 20, fontSize: '0.68rem', fontWeight: 500, fontStyle: 'italic' }}
                />
              );
            })()}
          </Box>
        );
      })}
    </Box>
  );
}
